<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useReferenceSelectionStore } from '../../stores/referenceSelectionStore';

const { t } = useI18n();

/**
 * "Select tags or create one" popover, shared by the bulk-actions bar
 * (applies to the checked rows) and the raw-view tag row (applies to the
 * single open reference).
 *
 * Operates on whatever content_hashes the parent passes; the parent owns
 * the trigger, open/close state, click-outside, and Escape handling. The
 * menu itself teleports to <body> and fixed-positions against the parent's
 * `anchorEl` (placement 'up': above, right-aligned; 'down': below,
 * left-aligned) so scroll containers can't clip it — in-menu clicks stop
 * propagating so the parent's window click-outside handler ignores them,
 * and any outside scroll emits `close` since the fixed menu would detach
 * from its anchor. The filter input autofocuses on open (the menu mounts
 * fresh each time it's shown).
 */

const props = withDefaults(
    defineProps<{
        contentHashes: string[];
        anchorEl?: HTMLElement | null;
        placement?: 'up' | 'down';
    }>(),
    { anchorEl: null, placement: 'up' },
);

const emit = defineEmits<{ close: [] }>();

const selectionStore = useReferenceSelectionStore();

const tagQuery = ref('');
const tagInputEl = ref<HTMLInputElement | null>(null);
const menuEl = ref<HTMLElement | null>(null);
const menuStyle = ref<Record<string, string>>({});

const MENU_GAP = 8;
const MENU_WIDTH = 260;

const position = () => {
    const rect = props.anchorEl?.getBoundingClientRect();
    if (!rect) return;
    if (props.placement === 'up') {
        menuStyle.value = {
            bottom: `${window.innerHeight - rect.top + MENU_GAP}px`,
            right: `${window.innerWidth - rect.right}px`,
        };
    } else {
        menuStyle.value = {
            top: `${rect.bottom + MENU_GAP}px`,
            left: `${Math.min(rect.left, window.innerWidth - MENU_WIDTH - MENU_GAP)}px`,
        };
    }
};

const filteredTags = computed(() => {
    const q = tagQuery.value.trim().toLowerCase();
    const matches = q
        ? selectionStore.tags.filter((t) => t.name.toLowerCase().includes(q))
        : [...selectionStore.tags];
    return matches.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
});

// Offer the Create row only for non-empty input that isn't already a tag.
const canCreate = computed(() => {
    const q = tagQuery.value.trim();
    if (!q) return false;
    return !selectionStore.tags.some((t) => t.name.toLowerCase() === q.toLowerCase());
});

const onToggleTag = (name: string) => {
    selectionStore.toggleTagOnRows(props.contentHashes, name);
};

const onCreateTag = () => {
    if (!canCreate.value) return;
    selectionStore.createAndApplyTag(tagQuery.value, props.contentHashes);
    tagQuery.value = '';
};

// The menu is position: fixed, so it would float in place while its anchor
// scrolls away — close it on any outside scroll instead (capture catches
// non-bubbling container scrolls; the menu's own list scroll is exempt).
const onScroll = (event: Event) => {
    if (menuEl.value && event.target instanceof Node && menuEl.value.contains(event.target)) {
        return;
    }
    emit('close');
};

onMounted(() => {
    position();
    window.addEventListener('scroll', onScroll, { capture: true });
    // Wait a tick for the popover to render before focusing.
    requestAnimationFrame(() => tagInputEl.value?.focus());
});

onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll, { capture: true });
});
</script>

<template>
    <Teleport to="body">
        <div ref="menuEl" class="tag-menu" :style="menuStyle" @click.stop>
            <input
                ref="tagInputEl"
                v-model="tagQuery"
                type="text"
                class="tag-menu-input"
                :placeholder="t('references.tagMenuPlaceholder')"
                @keydown.enter.prevent="onCreateTag"
            />
            <div class="tag-menu-list">
                <div
                    v-for="tag in filteredTags"
                    :key="tag.name"
                    class="tag-menu-option"
                    @click="onToggleTag(tag.name)"
                >
                    <span
                        class="reference-tag-pill"
                        :style="{
                            background: selectionStore.colorForTag(tag).bg,
                            color: selectionStore.colorForTag(tag).text,
                        }"
                        >{{ tag.name }}</span
                    >
                    <span
                        v-if="selectionStore.tagStateForRows(contentHashes, tag.name) === 'all'"
                        class="material-icons tag-menu-check"
                        >check</span
                    >
                    <span
                        v-else-if="
                            selectionStore.tagStateForRows(contentHashes, tag.name) === 'some'
                        "
                        class="material-icons tag-menu-check tag-menu-check-partial"
                        >remove</span
                    >
                </div>
                <div v-if="canCreate" class="tag-menu-option tag-menu-create" @click="onCreateTag">
                    <span class="tag-menu-create-label">{{ t('references.createTag') }}</span>
                    <span class="reference-tag-pill tag-menu-create-pill">{{
                        tagQuery.trim()
                    }}</span>
                </div>
                <div v-if="filteredTags.length === 0 && !canCreate" class="tag-menu-empty">
                    {{ t('references.tagMenuEmpty') }}
                </div>
            </div>
        </div>
    </Teleport>
</template>
